import React, { useEffect } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography
} from "@mui/material";
import { ReactComponent as Logo } from "assets/icons/logo.svg";
import { useActionHook, useNavigate } from "hooks";
import { useUserActions } from "store";

const DEFAULT_FILTERS = {
  asset_class: ["Private Equity", "Venture Capital", "Private Debt", "Real Estate"]
};

interface Props {
  sx?: SxProps<Theme>;
}

export const MarketIntelligence: React.FC<Props> = ({ sx }) => {
  const { getDashboardBenchmarkSummary, getDashboardMarketSignals } = useUserActions();
  const navigate = useNavigate();
  const {
    action: fetchBenchmark,
    data: benchmarkData,
    loading: benchmarkLoading,
    error: benchmarkError
  } = useActionHook(getDashboardBenchmarkSummary);
  const {
    action: fetchSignals,
    data: signalsData,
    loading: signalsLoading,
    error: signalsError
  } = useActionHook(getDashboardMarketSignals);

  useEffect(() => {
    fetchBenchmark({
      filters: DEFAULT_FILTERS,
      dateRange: {}
    });
    fetchSignals();
  }, []);

  return (
    <Stack
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
        overflow: "hidden",
        flex: "0 0 auto",
        width: "340px",
        padding: "16px",
        ...sx
      }}
    >
      {/* ── Header ───────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" gap="8px" sx={{ mb: "10px" }}>
        <Logo style={{ width: "24px", height: "24px" }} />
        <Typography sx={{ fontWeight: 500, color: "#071F35" }} variant="h6">
          Altius Market Intelligence
        </Typography>
      </Stack>

      {/* ── Body ─────────────────────────────────────────────── */}
      <Stack sx={{ gap: "20px" }}>
        {/* Altius Benchmark section */}
        <Stack gap="10px">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              sx={{
                textTransform: "uppercase",
                color: "#496682"
              }}
              variant="body4"
            >
              Altius Benchmark
            </Typography>
            <Button
              onClick={() => navigate({ route: "benchmark" })}
              size="sm"
              variant="outlined"
              sx={{
                fontSize: "12px",
                lineHeight: "16px",
                color: "#071F35",
                textTransform: "none"
              }}
            >
              Show all
            </Button>
          </Stack>

          {benchmarkLoading && (
            <Stack alignItems="center" justifyContent="center" sx={{ py: "20px" }}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {benchmarkError && !benchmarkLoading && (
            <Typography variant="body1" sx={{ color: "#496682", py: "8px" }}>
              Benchmark data could not be loaded. Refresh the page or contact support if the issue persists.
            </Typography>
          )}

          {!benchmarkLoading && !benchmarkError && benchmarkData && (
            <TableContainer sx={{ borderRadius: "6px", border: "1px solid #E5E8EF" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#252D39",
                        padding: "8px 10px",
                        borderBottom: "1px solid #E5E8EF",
                        backgroundColor: "#F8F9FD"
                      }}
                    >
                      Asset Class
                    </TableCell>
                    {benchmarkData.columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align="right"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#252D39",
                          padding: "8px 10px",
                          borderBottom: "1px solid #E5E8EF",
                          backgroundColor: "#F8F9FD"
                        }}
                      >
                        {col.title}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {benchmarkData.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex} sx={{ "&:last-child td": { borderBottom: "none" } }}>
                      <TableCell
                        align="left"
                        sx={{
                          fontSize: "13px",
                          color: "#252D39",
                          padding: "8px 10px",
                          borderBottom: "1px solid #E5E8EF"
                        }}
                      >
                        {row.title}
                      </TableCell>
                      {benchmarkData.columns.map((col) => {
                        const value = row.valueData[col.key];
                        const postfix = row.postfixes[col.key];
                        const displayValue = value !== undefined ? `${value}${postfix}` : "—";
                        return (
                          <TableCell
                            key={col.key}
                            align="right"
                            sx={{
                              fontSize: "13px",
                              color: "#252D39",
                              padding: "8px 10px",
                              borderBottom: "1px solid #E5E8EF"
                            }}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>

        {/* Market Signals section */}
        <Stack gap="8px">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              sx={{
                textTransform: "uppercase",
                color: "#496682"
              }}
              variant="body4"
            >
              Market Signals
            </Typography>
            {/* <Button
              size="sm"
              variant="outlined"
              sx={{
                fontSize: "12px",
                lineHeight: "16px",
                color: "#071F35",
                textTransform: "none"
              }}
            >
              Show all
            </Button> */}
          </Stack>

          {signalsLoading && (
            <Stack alignItems="center" justifyContent="center" sx={{ py: "20px" }}>
              <CircularProgress size={24} />
            </Stack>
          )}

          {signalsError && !signalsLoading && (
            <Typography variant="body1" sx={{ color: "#496682", py: "8px" }}>
              Market signals could not be loaded. Refresh the page or contact support if the issue persists.
            </Typography>
          )}

          {!signalsLoading && !signalsError && signalsData && signalsData.length === 0 && (
            <Typography variant="body1" sx={{ color: "#496682", py: "8px" }}>
              No market signals available yet. Check back after the next weekly update.
            </Typography>
          )}

          {!signalsLoading && !signalsError && signalsData && signalsData.length > 0 && (
            <Stack gap="10px">
              {signalsData.map((signal) => (
                <Stack key={signal.id} direction="row" alignItems="flex-start" gap="8px">
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: "#071F35",
                      flexShrink: 0,
                      mt: "7px"
                    }}
                  />
                  <Typography sx={{ color: "#071F35", lineHeight: 1.5 }} variant="body1">
                    {signal.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
